import React, { useEffect, useState } from 'react';
import api, { isStaffPlus, getCurrentUser } from '../api/expoApi';

export default function RouteDesigner() {
    const user = getCurrentUser();
    const canEdit = isStaffPlus(user?.role);
    const [routes, setRoutes] = useState([]);
    const [selected, setSelected] = useState(null);
    const [stops, setStops] = useState([]);
    const [newRoute, setNewRoute] = useState({ code: '', name: '', description: '' });
    const [newStop, setNewStop] = useState({ city: '', area: '', postal_code: '', deliver_window_start: '', deliver_window_end: '' });
    const [msg, setMsg] = useState('');

    const loadRoutes = async () => {
        try { const r = await api.get('/delivery-routes'); setRoutes(r.data?.data || []); }
        catch (e) { console.error(e); }
    };

    const loadStops = async (routeId) => {
        try {
            const r = await api.get(`/delivery-routes/${routeId}/stops`);
            setStops(r.data?.data || []);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { loadRoutes(); }, []);
    useEffect(() => { if (selected) loadStops(selected); }, [selected]);

    const createRoute = async () => {
        try {
            await api.post('/delivery-routes', newRoute);
            setMsg('Route created');
            setNewRoute({ code: '', name: '', description: '' });
            loadRoutes();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const addStop = async () => {
        try {
            await api.post(`/delivery-routes/${selected}/stops`, newStop);
            setNewStop({ city: '', area: '', postal_code: '', deliver_window_start: '', deliver_window_end: '' });
            loadStops(selected);
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const moveStop = async (idx, dir) => {
        const newOrder = [...stops];
        const target = idx + dir;
        if (target < 0 || target >= newOrder.length) return;
        [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
        const order = newOrder.map((s, i) => ({ id: s.stop_id, sequence: i + 1 }));
        try {
            await api.post(`/delivery-routes/${selected}/reorder`, { order });
            loadStops(selected);
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const deleteStop = async (stopId) => {
        if (!window.confirm('Delete this stop?')) return;
        try {
            await api.delete(`/delivery-routes/${selected}/stops/${stopId}`);
            loadStops(selected);
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const toggleExclude = async (stop) => {
        try {
            await api.put(`/delivery-routes/${selected}/stops/${stop.stop_id}`, { is_excluded: !stop.is_excluded });
            loadStops(selected);
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    return (
        <div style={{ padding: 24 }}>
            <h1>Route Designer</h1>
            {msg && <div style={{ background: '#d1e7dd', padding: 8, borderRadius: 4, marginBottom: 12 }}>{msg}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24 }}>
                <div>
                    <h3>Routes</h3>
                    {canEdit && (
                        <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                            <input placeholder="Code (e.g. KHI-N1)" value={newRoute.code} onChange={(e) => setNewRoute({...newRoute, code: e.target.value})} style={{ width: '100%', marginBottom: 4 }} />
                            <input placeholder="Name" value={newRoute.name} onChange={(e) => setNewRoute({...newRoute, name: e.target.value})} style={{ width: '100%', marginBottom: 4 }} />
                            <input placeholder="Description" value={newRoute.description} onChange={(e) => setNewRoute({...newRoute, description: e.target.value})} style={{ width: '100%', marginBottom: 4 }} />
                            <button onClick={createRoute} style={{ width: '100%' }}>Create Route</button>
                        </div>
                    )}
                    <div>
                        {routes.map((r) => (
                            <div key={r.route_id}
                                onClick={() => setSelected(r.route_id)}
                                style={{
                                    padding: 10, marginBottom: 6, borderRadius: 4, cursor: 'pointer',
                                    background: selected === r.route_id ? '#0d6efd' : '#fff',
                                    color: selected === r.route_id ? '#fff' : '#333',
                                    border: '1px solid #ddd'
                                }}>
                                <strong>{r.code}</strong> — {r.name}
                                <div style={{ fontSize: '.8rem', opacity: .8 }}>
                                    {r.hub_code} · {r.stop_count} stops
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    {selected ? (
                        <>
                            <h3>Stops</h3>
                            {canEdit && (
                                <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                                        <input placeholder="City" value={newStop.city} onChange={(e) => setNewStop({...newStop, city: e.target.value})} />
                                        <input placeholder="Area" value={newStop.area} onChange={(e) => setNewStop({...newStop, area: e.target.value})} />
                                        <input placeholder="Postal code" value={newStop.postal_code} onChange={(e) => setNewStop({...newStop, postal_code: e.target.value})} />
                                        <input type="time" value={newStop.deliver_window_start} onChange={(e) => setNewStop({...newStop, deliver_window_start: e.target.value})} />
                                        <input type="time" value={newStop.deliver_window_end} onChange={(e) => setNewStop({...newStop, deliver_window_end: e.target.value})} />
                                    </div>
                                    <button onClick={addStop} style={{ marginTop: 6 }}>Add Stop</button>
                                </div>
                            )}
                            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                                <thead><tr><th>Seq</th><th>City</th><th>Area</th><th>Window</th><th>Excluded?</th><th>Actions</th></tr></thead>
                                <tbody>
                                    {stops.map((s, i) => (
                                        <tr key={s.stop_id} style={s.is_excluded ? { opacity: 0.5 } : {}}>
                                            <td>{s.sequence}</td>
                                            <td>{s.city}</td>
                                            <td>{s.area}</td>
                                            <td>{s.deliver_window_start} – {s.deliver_window_end}</td>
                                            <td>{s.is_excluded ? '✓' : ''}</td>
                                            <td>
                                                {canEdit && (
                                                    <>
                                                        <button onClick={() => moveStop(i, -1)} disabled={i === 0}>↑</button>
                                                        <button onClick={() => moveStop(i, +1)} disabled={i === stops.length - 1}>↓</button>
                                                        <button onClick={() => toggleExclude(s)}>{s.is_excluded ? 'Include' : 'Exclude'}</button>
                                                        <button onClick={() => deleteStop(s.stop_id)} style={{ color: '#dc3545' }}>×</button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <p style={{ color: '#888' }}>Select a route to view its stops.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
